/**
 * Pure, dependency-free IP allowlist matching.
 *
 * Runs on Cloudflare Workers, so this module deliberately avoids every Node
 * built-in and external package — all address parsing and CIDR masking is
 * hand-rolled with BigInt. It is the enforcement core for an organization IP
 * allowlist, so correctness is a security property: a bug here either locks
 * legitimate customers out or lets blocked traffic through. Treat the test
 * suite as the specification.
 *
 * Supported inputs (both `parseIpRule` and `ipMatchesRule` client IPs):
 *   - IPv4 dotted-quad ("203.0.113.5")
 *   - IPv6 ("2001:db8::1"), including a single "::" zero-compression run and a
 *     trailing embedded IPv4 tail ("::ffff:192.168.0.1", "2001:db8::192.168.0.1")
 *   - CIDR forms of either family ("203.0.113.0/24", "2001:db8::/32")
 *
 * IPv4-mapped IPv6 (the ::ffff:0:0/96 range, e.g. "::ffff:203.0.113.5") is
 * canonicalized to its embedded IPv4 during parsing, so a v4 rule matches a
 * v4-mapped client and a v4-mapped rule matches a v4 client. A genuine v6
 * address never matches a v4 rule and vice versa.
 */

/** Address family of a rule or address. */
export type IpFamily = 'v4' | 'v6';

export interface IpRule {
  /**
   * Canonical CIDR string.
   *
   * Canonicalization is applied on parse so equal networks share one string:
   *   - v4: dotted-quad network address + "/" + prefix, e.g. "203.0.113.5/32".
   *     Octets have no leading zeros. The stored address is the raw input's
   *     network address (host bits are NOT zeroed — "203.0.113.5/24" keeps the
   *     ".5"); matching masks host bits regardless.
   *   - v6: lowercase, fully "::"-compressed form + "/" + prefix, e.g.
   *     "2001:db8::/32". IPv4-mapped inputs are rewritten to v4 form, so
   *     "::ffff:203.0.113.5" parses to the v4 rule "203.0.113.5/32".
   */
  readonly cidr: string;
  /** Address family. */
  readonly family: IpFamily;
}

/** An address parsed to its numeric value plus family. Internal. */
interface ParsedAddress {
  readonly family: IpFamily;
  /** 32-bit value for v4, 128-bit value for v6. */
  readonly bits: bigint;
}

const V4_WIDTH = 32;
const V6_WIDTH = 128;
const V4_MAX_PREFIX = 32;
const V6_MAX_PREFIX = 128;

const V4_MASK = (1n << 32n) - 1n;
const HEXTET_MASK = 0xffffn;

/**
 * The ::ffff:0:0/96 IPv4-mapped range. An address whose top 96 bits equal this
 * prefix carries an IPv4 address in its low 32 bits.
 */
const V4_MAPPED_PREFIX = 0xffffn << 32n; // bits 32..47 set, rest of high 96 zero

type ParseResult = { ok: true; rule: IpRule } | { ok: false; error: string };

function err(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

/**
 * Parse a bare IPv4 dotted-quad into its 32-bit value. Requires exactly four
 * decimal octets in 0..255 with no leading zeros (to reject ambiguous/octal
 * forms) and no extra characters. Returns null on any deviation.
 */
function parseIpv4(input: string): bigint | null {
  const parts = input.split('.');
  if (parts.length !== 4) return null;
  let value = 0n;
  for (const part of parts) {
    // Non-empty, digits only. Rejects "", "+1", "0x1", " 1", "1 ".
    if (part.length === 0 || part.length > 3) return null;
    if (!/^[0-9]+$/.test(part)) return null;
    // Reject leading zeros ("01", "00") to avoid octal ambiguity — but allow "0".
    if (part.length > 1 && part[0] === '0') return null;
    const n = Number(part);
    if (n > 255) return null;
    value = (value << 8n) | BigInt(n);
  }
  return value;
}

/**
 * Parse a bare IPv6 address (optionally with a single "::" run and/or a
 * trailing embedded IPv4 tail) into its 128-bit value. Returns null on any
 * malformation: more than one "::", wrong hextet count, bad hextet, or a bad
 * embedded v4 tail.
 */
function parseIpv6(input: string): bigint | null {
  if (input.length === 0) return null;
  // A zone id ("%eth0") is not meaningful for allowlist matching; reject it.
  if (input.includes('%')) return null;

  // Split on "::" at most once. More than one "::" is invalid.
  const doubleColonCount = input.split('::').length - 1;
  if (doubleColonCount > 1) return null;

  let head: string;
  let tail: string;
  let compressed: boolean;
  if (doubleColonCount === 1) {
    compressed = true;
    const idx = input.indexOf('::');
    head = input.slice(0, idx);
    tail = input.slice(idx + 2);
  } else {
    compressed = false;
    head = input;
    tail = '';
  }

  const headGroups = head.length === 0 ? [] : head.split(':');
  const tailGroups = tail.length === 0 ? [] : tail.split(':');

  // An empty group (from ":::", a leading single ":", or a trailing single ":")
  // produced a zero-length split segment — malformed.
  if (headGroups.some((g) => g.length === 0)) return null;
  if (tailGroups.some((g) => g.length === 0)) return null;

  // A trailing embedded IPv4 (e.g. "::ffff:1.2.3.4") occupies the final group
  // slot of the WHOLE address and expands to two hextets. With "::" that final
  // group lives at the end of the tail; without "::" it lives at the end of the
  // head. A "." in any other group is malformed.
  const finalGroups = compressed ? tailGroups : headGroups;
  const otherGroups = compressed ? headGroups : tailGroups;
  if (otherGroups.some((g) => g.includes('.'))) return null;
  const finalGroup = finalGroups.length > 0 ? finalGroups[finalGroups.length - 1] : undefined;
  if (finalGroups.some((g, i) => g.includes('.') && i !== finalGroups.length - 1)) return null;

  let embeddedV4: bigint | null = null;
  if (finalGroup?.includes('.')) {
    embeddedV4 = parseIpv4(finalGroup);
    if (embeddedV4 === null) return null;
  }

  const embeddedHextets = embeddedV4 === null ? 0 : 2;
  // The embedded v4 group replaces one text group with two hextets, so subtract
  // its group from the raw count before adding the two hextets it represents.
  const headCount = compressed || embeddedV4 === null ? headGroups.length : headGroups.length - 1;
  const tailCount = compressed && embeddedV4 !== null ? tailGroups.length - 1 : tailGroups.length;
  const explicitHextets = headCount + tailCount + embeddedHextets;

  // A full v6 address is 8 hextets. Without "::" the groups must total exactly
  // 8. With "::" they must total fewer than 8 (the "::" fills at least one
  // zero group).
  if (!compressed) {
    if (explicitHextets !== 8) return null;
  } else {
    if (explicitHextets >= 8) return null;
  }

  // Build the 128-bit value: head hextets, then the compressed zero run, then
  // tail hextets, then the embedded v4 (as its 32 low bits of the final slot).
  let value = 0n;

  const pushHextet = (group: string): boolean => {
    if (group.length === 0 || group.length > 4) return false;
    if (!/^[0-9a-fA-F]+$/.test(group)) return false;
    const n = BigInt(parseInt(group, 16));
    value = (value << 16n) | (n & HEXTET_MASK);
    return true;
  };

  const headHextetGroups = embeddedV4 !== null && !compressed ? headGroups.slice(0, -1) : headGroups;
  const tailHextetGroups = embeddedV4 !== null && compressed ? tailGroups.slice(0, -1) : tailGroups;

  for (const g of headHextetGroups) {
    if (!pushHextet(g)) return null;
  }

  if (compressed) {
    const zeroHextets = 8 - explicitHextets;
    for (let i = 0; i < zeroHextets; i++) {
      value = value << 16n; // shift in a zero hextet
    }
  }

  for (const g of tailHextetGroups) {
    if (!pushHextet(g)) return null;
  }

  if (embeddedV4 !== null) {
    // The embedded v4 occupies the final two hextets (32 bits).
    value = (value << 32n) | (embeddedV4 & V4_MASK);
  }

  // Sanity: the result must fit in 128 bits.
  if (value < 0n || value >= 1n << 128n) return null;

  return value;
}

/**
 * If a parsed v6 value is an IPv4-mapped address (::ffff:0:0/96), return its
 * embedded IPv4 value; otherwise null.
 */
function extractV4Mapped(v6bits: bigint): bigint | null {
  const high96 = v6bits >> 32n;
  if (high96 === V4_MAPPED_PREFIX >> 32n) {
    return v6bits & V4_MASK;
  }
  return null;
}

/**
 * Parse a bare address string (no prefix) into its numeric value, canonicalizing
 * IPv4-mapped IPv6 to v4. Returns null on invalid input.
 */
function parseAddress(input: string): ParsedAddress | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  // A ":" means it is (or is meant to be) IPv6. Otherwise treat as IPv4. This
  // also correctly routes IPv4-mapped forms ("::ffff:1.2.3.4") through the v6
  // parser, which then gets normalized to v4 below.
  if (trimmed.includes(':')) {
    const v6 = parseIpv6(trimmed);
    if (v6 === null) return null;
    const mapped = extractV4Mapped(v6);
    if (mapped !== null) {
      return { family: 'v4', bits: mapped };
    }
    return { family: 'v6', bits: v6 };
  }

  const v4 = parseIpv4(trimmed);
  if (v4 === null) return null;
  return { family: 'v4', bits: v4 };
}

/** Format a 32-bit value as a dotted-quad string. */
function formatIpv4(bits: bigint): string {
  const a = (bits >> 24n) & 0xffn;
  const b = (bits >> 16n) & 0xffn;
  const c = (bits >> 8n) & 0xffn;
  const d = bits & 0xffn;
  return `${a}.${b}.${c}.${d}`;
}

/**
 * Format a 128-bit value as a canonical lowercase, "::"-compressed IPv6 string.
 * Compresses the single longest run of two-or-more zero hextets (leftmost on a
 * tie), matching RFC 5952.
 */
function formatIpv6(bits: bigint): string {
  const hextets: number[] = [];
  for (let i = 7; i >= 0; i--) {
    hextets.push(Number((bits >> BigInt(i * 16)) & HEXTET_MASK));
  }

  // Find the longest run of consecutive zero hextets (length >= 2).
  let bestStart = -1;
  let bestLen = 0;
  let curStart = -1;
  let curLen = 0;
  for (let i = 0; i < 8; i++) {
    if (hextets[i] === 0) {
      if (curStart === -1) curStart = i;
      curLen += 1;
      if (curLen > bestLen) {
        bestLen = curLen;
        bestStart = curStart;
      }
    } else {
      curStart = -1;
      curLen = 0;
    }
  }

  if (bestLen < 2) {
    return hextets.map((h) => h.toString(16)).join(':');
  }

  const before = hextets.slice(0, bestStart).map((h) => h.toString(16));
  const after = hextets.slice(bestStart + bestLen).map((h) => h.toString(16));
  return `${before.join(':')}::${after.join(':')}`;
}

/**
 * Parse a user-entered IP or CIDR string into a canonical {@link IpRule}.
 *
 * Accepts a bare IPv4 (→ /32), a bare IPv6 (→ /128), or an explicit CIDR of
 * either family. Whitespace is trimmed. Never throws — returns a discriminated
 * result. IPv4-mapped IPv6 inputs are canonicalized to v4.
 */
export function parseIpRule(input: string): ParseResult {
  if (typeof input !== 'string') return err('IP rule must be a string.');
  const trimmed = input.trim();
  if (trimmed.length === 0) return err('IP rule is empty.');

  const slashCount = trimmed.split('/').length - 1;
  if (slashCount > 1) return err('IP rule has more than one "/".');

  let addrPart: string;
  let prefixPart: string | null;
  if (slashCount === 1) {
    const idx = trimmed.indexOf('/');
    addrPart = trimmed.slice(0, idx);
    prefixPart = trimmed.slice(idx + 1);
  } else {
    addrPart = trimmed;
    prefixPart = null;
  }

  const parsed = parseAddress(addrPart);
  if (parsed === null) return err(`Invalid IP address: "${addrPart}".`);

  const maxPrefix = parsed.family === 'v4' ? V4_MAX_PREFIX : V6_MAX_PREFIX;

  let prefix: number;
  if (prefixPart === null) {
    prefix = maxPrefix; // bare address → host route
  } else {
    if (prefixPart.length === 0) return err('CIDR prefix is empty.');
    if (!/^[0-9]+$/.test(prefixPart)) return err(`Invalid CIDR prefix: "${prefixPart}".`);
    prefix = Number(prefixPart);
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > maxPrefix) {
      return err(`CIDR prefix out of range (0-${maxPrefix}): "${prefixPart}".`);
    }
  }

  const cidr =
    parsed.family === 'v4'
      ? `${formatIpv4(parsed.bits)}/${prefix}`
      : `${formatIpv6(parsed.bits)}/${prefix}`;

  return { ok: true, rule: { cidr, family: parsed.family } };
}

/**
 * Split a canonical cidr string into its numeric network value and prefix.
 * The cidr comes from {@link parseIpRule} so it is well-formed; we re-parse
 * defensively and return null if it somehow is not.
 */
function decodeRule(rule: IpRule): { bits: bigint; prefix: number; family: IpFamily } | null {
  const slashIdx = rule.cidr.lastIndexOf('/');
  if (slashIdx === -1) return null;
  const addrPart = rule.cidr.slice(0, slashIdx);
  const prefixPart = rule.cidr.slice(slashIdx + 1);

  const parsed = parseAddress(addrPart);
  if (parsed === null) return null;
  if (parsed.family !== rule.family) return null;

  if (!/^[0-9]+$/.test(prefixPart)) return null;
  const prefix = Number(prefixPart);
  const maxPrefix = parsed.family === 'v4' ? V4_MAX_PREFIX : V6_MAX_PREFIX;
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > maxPrefix) return null;

  return { bits: parsed.bits, prefix, family: parsed.family };
}

/**
 * True iff two same-width values agree on their top `prefix` bits. `prefix` 0
 * matches everything of that width; `prefix === width` requires exact equality.
 */
function bitsMatchUnderPrefix(a: bigint, b: bigint, prefix: number, width: number): boolean {
  if (prefix === 0) return true;
  const hostBits = BigInt(width - prefix);
  return a >> hostBits === b >> hostBits;
}

/**
 * True if the given client IP (a bare address string) falls within `rule`.
 *
 * Handles v4 exact + CIDR, v6 exact + CIDR (with "::" expansion), and
 * IPv4-mapped IPv6 on either side (normalized to v4). Cross-family never
 * matches. An unparseable client IP returns false (fail closed for "matches").
 */
export function ipMatchesRule(clientIp: string, rule: IpRule): boolean {
  if (typeof clientIp !== 'string') return false;

  const client = parseAddress(clientIp);
  if (client === null) return false;

  const decoded = decodeRule(rule);
  if (decoded === null) return false;

  if (client.family !== decoded.family) return false;

  const width = client.family === 'v4' ? V4_WIDTH : V6_WIDTH;
  return bitsMatchUnderPrefix(client.bits, decoded.bits, decoded.prefix, width);
}

/**
 * True if the client IP matches ANY rule.
 *
 * An empty rule array returns false — this function answers only "does it match
 * one of these"; the caller decides whether "no rules configured" means "allow
 * all" (the usual allowlist convention) or "deny all".
 */
export function ipMatchesAny(clientIp: string, rules: readonly IpRule[]): boolean {
  for (const rule of rules) {
    if (ipMatchesRule(clientIp, rule)) return true;
  }
  return false;
}
